import React, { useState, useCallback } from "react";
import { RotateCw } from "lucide-react";
import { speechService } from "../../services/speechService";
import { NO_TEMPLATE_MESSAGE, RETRY_FAILED_MESSAGE } from "../../utils/retryEligibility";

interface RetryButtonProps {
  recordingId: string;
  templateId: number | string | null | undefined;
  recordingTitle?: string;
  // Fired the moment the POST succeeds, so callers can optimistically flip the
  // recording into the normal "Processing" UI and resume polling/WebSocket flow.
  onRetryStarted?: (recordingId: string) => void;
  // Fired on non-200/network failure, after the button has restored itself.
  onRetryError?: (recordingId: string, message: string) => void;
  size?: "sm" | "md";
  className?: string;
  // Stop propagation so clicking Retry inside a clickable list row doesn't also select it.
  stopPropagation?: boolean;
}

export const RetryButton: React.FC<RetryButtonProps> = ({
  recordingId,
  templateId,
  recordingTitle,
  onRetryStarted,
  onRetryError,
  size = "sm",
  className = "",
  stopPropagation = true,
}) => {
  const [isRetrying, setIsRetrying] = useState(false);

  const hasTemplate = templateId !== null && templateId !== undefined;

  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      if (stopPropagation) {
        e.stopPropagation();
      }

      // Guard double-clicks - the endpoint is not idempotent, each call queues a new job.
      if (isRetrying || !hasTemplate) return;

      setIsRetrying(true);
      try {
        await speechService.retryProcessing(recordingId, templateId as number | string);
        onRetryStarted?.(recordingId);
      } catch (error: any) {
        console.error(`Retry processing failed for recording ${recordingId}:`, error?.response?.data ?? error);
        onRetryError?.(recordingId, RETRY_FAILED_MESSAGE);
        setIsRetrying(false);
      }
    },
    [isRetrying, hasTemplate, recordingId, templateId, onRetryStarted, onRetryError, stopPropagation]
  );

  const label = recordingTitle
    ? `Retry processing for ${recordingTitle}`
    : "Retry processing";

  const sizeClasses = size === "sm" ? "text-xs px-2 py-1" : "text-sm px-3 py-1.5";

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isRetrying || !hasTemplate}
      aria-label={label}
      title={!hasTemplate ? NO_TEMPLATE_MESSAGE : undefined}
      className={`inline-flex items-center gap-1.5 font-medium rounded-full transition-colors ${sizeClasses} ${
        isRetrying || !hasTemplate
          ? "bg-gray-100 text-gray-400 cursor-not-allowed"
          : "bg-red-50 text-red-700 hover:bg-red-100"
      } ${className}`}
    >
      <RotateCw className={`${size === "sm" ? "w-3 h-3" : "w-3.5 h-3.5"} ${isRetrying ? "animate-spin" : ""}`} />
      {isRetrying ? "Retrying…" : "Retry"}
    </button>
  );
};

export default RetryButton;
